import { motion } from 'framer-motion';
import { Heart, Star, Truck, ShieldCheck } from '@phosphor-icons/react';

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <section className="relative h-80 md:h-96 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1707968781591-59ff287a54c6?w=1600" alt="Beautiful home interior" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nexa-charcoal/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-white mb-4">About Nexaorbits</h1>
            <p className="text-white/80 max-w-xl mx-auto px-6">Curating beautiful home decor since 2020</p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">Our Story</p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal mb-6">Transforming Houses Into Homes</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>Nexaorbits was born from a simple belief: your home should be a reflection of your personality, a sanctuary where every piece tells a story. Founded in 2020, we set out to curate exceptional home decor that brings warmth, elegance, and character to living spaces across America.</p>
                <p>Our journey began with a passion for design and a commitment to quality. We handpick each item in our collection, working directly with skilled artisans and trusted manufacturers who share our vision of creating beautiful, lasting pieces.</p>
                <p>Today, we're proud to have helped thousands of customers transform their houses into homes they love. From statement wall art to delicate ceramics, every Nexaorbits piece is chosen with care and designed to inspire.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="aspect-[4/5] rounded-lg overflow-hidden">
              <img src="https://images.unsplash.com/photo-1723257133428-fc8f16185cf0?w=800" alt="Beautifully decorated room" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-nexa-stone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">Our Values</p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Star, title: 'Quality First', description: 'We never compromise on quality. Every product is carefully selected to meet our high standards.' },
              { icon: Heart, title: 'Customer Love', description: 'Your satisfaction is our priority. We go above and beyond to ensure you love your purchase.' },
              { icon: Truck, title: 'Fast Delivery', description: 'We understand the excitement of receiving your order. Thats why we offer fast, reliable shipping.' },
              { icon: ShieldCheck, title: 'Trust & Safety', description: 'Shop with confidence. Your security and privacy are protected with industry-leading measures.' }
            ].map((value, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="bg-white p-8 rounded-lg text-center">
                <value.icon size={40} className="text-nexa-gold mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold text-nexa-charcoal mb-3">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">Our Promise</p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal mb-6">Your Satisfaction Guaranteed</h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">We stand behind every product we sell. If you're not completely satisfied with your purchase, we offer a hassle-free 30-day return policy. Our dedicated customer support team is always here to help you find the perfect pieces for your home.</p>
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2"><ShieldCheck size={20} className="text-nexa-gold" /><span className="text-nexa-charcoal font-medium">Secure Payments</span></div>
            <div className="flex items-center gap-2"><Truck size={20} className="text-nexa-gold" /><span className="text-nexa-charcoal font-medium">Free Shipping $75+</span></div>
            <div className="flex items-center gap-2"><Star size={20} className="text-nexa-gold" /><span className="text-nexa-charcoal font-medium">Premium Quality</span></div>
          </div>
        </div>
      </section>
    </div>
  );
}
